import {
  Box,
  Typography,
  useInterstateToken
} from "@dealer-center/core-components";
import type {
  NrtStatus,
  NrtTestStatus
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { buildChipSx } from "./chipSx";

// Covers both the records table's NrtStatus chip and the Test Result panel's
// NrtTestStatus chip — the two vocabularies are disjoint, so one map serves both.
export type ChipStatus = NrtStatus | NrtTestStatus;

export interface StatusChipProps {
  status: ChipStatus;
}

// IDLE/RUNNING read as prose; every other status renders its own value verbatim.
export const STATUS_LABEL: Record<ChipStatus, string> = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  TESTING: "TESTING",
  IDLE: "Not run",
  RUNNING: "Running",
  COMPLETE: "COMPLETE",
  ERROR: "ERROR"
};

// TESTING/IDLE/RUNNING have no Interstate token yet, so they fall back to
// FALLBACK_COLOR below.
const STATUS_TOKEN: Partial<Record<ChipStatus, string>> = {
  ACTIVE: "sem.color.on-surface.success.default",
  INACTIVE: "sem.color.on-surface.error.default",
  COMPLETE: "sem.color.on-surface.success.default",
  ERROR: "sem.color.on-surface.error.default"
};

// ACTIVE's green only clears ~4.35:1 contrast; COMPLETE uses a darker green
// to clear the 5:1 bar.
const FALLBACK_COLOR: Record<ChipStatus, string> = {
  ACTIVE: "#0f7a4d",
  INACTIVE: "#b3261e",
  TESTING: "#6b4500",
  IDLE: "#44474a",
  RUNNING: "#1a4d8f",
  COMPLETE: "#0a5c3a",
  ERROR: "#b3261e"
};

const getStatusColor = (
  status: ChipStatus,
  getToken: ReturnType<typeof useInterstateToken>
): string => {
  const token = STATUS_TOKEN[status];
  return (
    (token ? getToken<string>(token) : undefined) ?? FALLBACK_COLOR[status]
  );
};

export const StatusChip = ({ status }: StatusChipProps) => {
  const getToken = useInterstateToken();
  const color = getStatusColor(status, getToken);

  return (
    <Box sx={buildChipSx(color)}>
      <Typography sx={{ color, fontWeight: 600 }} variant="body-sm">
        {STATUS_LABEL[status]}
      </Typography>
    </Box>
  );
};
